import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

export function replaceCreatedAtFilter(sql: string, startDate: string | Date, endDate: string | Date): string {
    const startStr = dayjs(startDate).tz(dayjs.tz.guess()).format();
    const endStr = dayjs(endDate).tz(dayjs.tz.guess()).format();

    // 匹配 created_at 条件
    // 兼容 >=, >, <=, <, =
    // 兼容 BETWEEN ... AND ...
    // 兼容值包含: 字符串 '...', 括号 (...), 或非关键词字符
    // 停止于 AND, OR, UNION, GROUP BY 等关键词
    
    // 关键词列表，遇到这些词时应停止匹配值
    const stopWords = "AND|OR|UNION|GROUP|ORDER|LIMIT|HAVING|OFFSET|FOR|WINDOW";
    
    // 值模式：引号字符串 | 括号内容 | 非关键词且非特殊字符的连续串 | 空白符
    const valuePattern = `(?:'[^']*'|\\((?:[^()]|\\([^()]*\\))*\\)|(?!(?:${stopWords})\\b)[^'"()\\s]+|\\s+)`;
    
    // BETWEEN 的第一个值模式：不能匹配到 AND
    const betweenValue1 = `(?:'[^']*'|\\((?:[^()]|\\([^()]*\\))*\\)|(?!(?:AND)\\b)[^'"()\\s]+|\\s+)`;
    
    const createdAtRegex = new RegExp(
        `\\b(?:([\\w]+)\\.)?(created_at|occurred_at)(?:\\s+AT\\s+TIME\\s+ZONE\\s+'[^']+')?\\s*(?:(?:>=|>|<=|<|=)\\s*${valuePattern}+|BETWEEN\\s+${betweenValue1}+\\s+AND\\s+${valuePattern}+)`,
        'gi'
    );

    if (createdAtRegex.test(sql)) {
        // 替换所有匹配的 created_at/occurred_at 条件
        let newSql = sql.replace(createdAtRegex, (match, alias, colName) => {
            const prefix = alias ? `${alias}.` : '';
            const col = colName.toLowerCase();
            let replacement = `${prefix}${col} >= '${startStr}' AND ${prefix}${col} <= '${endStr}'`;
            
            // 保留尾部的空白符（换行、空格等），以保持格式
            const trailingSpace = match.match(/(\s+)$/);
            if (trailingSpace) {
                replacement += trailingSpace[0];
            }
            
            return replacement;
        });

        // 清理由于移除条件留下的多余 AND/OR
        const cleanupOperators = (s: string) => {
            // 1. 去重相邻的相同时间条件 (e.g. "Range AND Range" -> "Range")
            const escapedStart = startStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const escapedEnd = endStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            
            // 匹配: (Range) + 任意空白 + AND + 任意空白 + \1 (Backreference to Range)
            const rangePattern = `((?:[\\w]+\\.)?(?:created_at|occurred_at) >= '${escapedStart}' AND (?:[\\w]+\\.)?(?:created_at|occurred_at) <= '${escapedEnd}')`;
            const dedupRegex = new RegExp(`${rangePattern}\\s+AND\\s+\\1`, 'gi');
            
            let temp = s;
            // 循环去重，以防有3个或更多
            while (dedupRegex.test(temp)) {
                temp = temp.replace(dedupRegex, '$1');
            }

            return temp
                .replace(/\bAND\s+AND\b/gi, 'AND')
                .replace(/\bOR\s+OR\b/gi, 'OR')
                .replace(/\bWHERE\s+(?:AND|OR)\b/gi, 'WHERE')
                .replace(/\(\s*(?:AND|OR)\b/gi, '(')
                .replace(/\b(?:AND|OR)\s*\)/gi, ')')
                .replace(/\b(?:AND|OR)\s*$/gi, '');
        };

        let prev = newSql;
        let curr = newSql;
        do {
            prev = curr;
            curr = cleanupOperators(curr);
        } while (prev !== curr);

        return curr;
    } else {
        // SQL 中没有 created_at 条件，不进行任何修改
        return sql;
    }
}

